import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Clock, User, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !pin) {
      toast.error('Preencha todos os campos');
      return;
    }

    setLoading(true);
    
    try {
      const res = await fetch(`${API}/portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, pin })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Credenciais inválidas');
      }

      toast.success('Login realizado com sucesso');
      navigate('/portal/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="border-b border-[#E5E7EB] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#111111] flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-black tracking-tight" style={{ fontFamily: 'Satoshi, sans-serif' }}>
              TimeTrack Bio
            </span>
          </a>
        </div>
      </header>

      {/* Login Form */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#0029FF] flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-black tracking-tight mb-2" style={{ fontFamily: 'Satoshi, sans-serif' }}>
              Portal do Colaborador
            </h1>
            <p className="text-[#6B7280]">
              Acesse seu histórico de ponto e faça solicitações
            </p>
          </div>

          <form onSubmit={handleSubmit} className="border border-[#E5E7EB] p-6 bg-white">
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Email</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                  <Input
                    data-testid="portal-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest">PIN</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                  <Input
                    data-testid="portal-pin-input"
                    type="password"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    placeholder="••••••"
                    maxLength={6}
                    className="pl-10"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#111111] hover:bg-[#0029FF]"
                disabled={loading}
                data-testid="portal-login-btn"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Entrando...
                  </>
                ) : (
                  'Entrar'
                )}
              </Button>
            </div>
          </form>

          <p className="text-center mt-4 text-sm text-[#6B7280]">
            Não tem acesso? Solicite seu PIN ao administrador.
          </p>
        </div>
      </main>
    </div>
  );
}
